// src/hooks/useTestingModeState.ts
// Hook providing testing mode state to Suite Shell.
// Returns null if not in dev/staging or user is not internal.

import { useAppSelector } from '../store/store';
import { selectBootMeta, selectBootUser } from '../store/slices/bootContextSlice';
import type { TestingModeState } from '@levelup/ui-shell';

/**
 * Check if current environment is dev or staging.
 */
function isDevOrStaging(): boolean {
  // Vite dev mode
  if (import.meta.env?.DEV === true) return true;

  // Hostname checks for deployed staging environments
  const hostname = window.location.hostname;
  if (hostname.includes('staging')) return true;
  if (hostname.includes('dev.')) return true;
  if (hostname.includes('localhost')) return true;
  if (hostname.includes('127.0.0.1')) return true;

  return false;
}

/**
 * Hook providing testing mode state to Suite Shell.
 * Returns null if not in dev/staging or user is not internal.
 */
export function useTestingModeState(): TestingModeState | null {
  const meta = useAppSelector(selectBootMeta);
  const user = useAppSelector(selectBootUser);

  // Guard: dev/staging only
  if (!isDevOrStaging()) return null;

  // Guard: internal users only
  const isInternal = user?.platformInternal || user?.platformSuperAdmin;
  if (!isInternal) return null;

  // Read authoritative state from meta.testingMode
  const testingMode = meta?.testingMode;

  return {
    isActive: testingMode?.active ?? false,
    override: testingMode?.override
      ? {
          workspaceId: testingMode.override.workspaceId,
          workspaceName: testingMode.override.workspaceName,
          appId: testingMode.override.appId,
          appName: testingMode.override.appName,
          role: testingMode.override.role,
        }
      : undefined,
  };
}
